/**
 * Rich per-service content used by ServiceDetail.jsx.
 * Keyed by slug. Only slugs present here get the enhanced layout;
 * everything else falls back to the original short template.
 *
 * Every string uses coordination-safe language ("we help coordinate",
 * "where available", "the treating doctor determines").
 * Do NOT diagnose, guarantee timings, or claim 24/7 unless explicit.
 */
export const SERVICE_CONTENT = {
  "doctor-to-hotel": {
    seoTitle: "Doctor to Hotel in Vietnam | Home & Hotel Visit Medical Support",
    metaDescription:
      "Help coordinating an independent licensed doctor to visit your hotel, apartment or home in Vietnam for non-emergency medical concerns. English-speaking coordination.",
    h1: "Doctor to Hotel in Vietnam",
    opening:
      "Help coordinating an independent licensed doctor to visit your hotel, apartment or home for non-emergency medical concerns where an in-person assessment may be appropriate.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You feel too unwell to travel to a clinic and want a doctor to come to you.",
        "You are a family, traveller or resident staying in a hotel, Airbnb or apartment.",
        "Your symptoms are non-emergency but you'd like an in-person assessment.",
        "You would prefer English-speaking coordination from your first message.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Message us on WhatsApp or use the request form." },
      { title: "Share the essentials", text: "Symptoms, location (hotel/apartment) and preferred timing." },
      { title: "We check availability", text: "We look for a suitable independent doctor for your area." },
      { title: "We coordinate the visit", text: "You get clear next steps and stay in touch with us throughout." },
    ],
    coordinate: {
      title: "What a visiting doctor may be able to do",
      note: "Depending on the doctor, circumstances and clinical assessment, a visit may include:",
      items: [
        "Medical history and symptom review",
        "Physical examination",
        "Basic observations",
        "Advice on appropriate next steps",
        "Treatment recommendations from the treating doctor",
        "Prescription where clinically appropriate and legally permitted",
        "Referral to a clinic, testing or hospital if needed",
      ],
    },
    commonReasons: {
      title: "Common reasons travellers request a doctor",
      note: "This list is not a diagnosis — please let us know your symptoms and we can help coordinate.",
      chips: [
        "Fever", "Food poisoning symptoms", "Vomiting or diarrhoea", "Stomach problems",
        "Cold or flu symptoms", "Sore throat", "Cough", "Ear pain",
        "Rash or skin problem", "Urinary symptoms", "Headache", "Dehydration", "Minor wounds or injuries",
      ],
    },
    whatToExpect: [
      { q: "What information will I need to provide?", a: "Your name, location (hotel/apartment), main symptoms, when they started and any relevant medical history or current medicines." },
      { q: "Will the doctor speak English?", a: "Our coordination is English-speaking, and we help match you with an English-speaking doctor where available." },
      { q: "Where does the appointment happen?", a: "In your hotel room, serviced apartment, Airbnb or home — wherever you're staying." },
      { q: "How is availability confirmed?", a: "We contact suitable independent doctors and confirm availability individually before anything is arranged." },
      { q: "What if I need a different level of care?", a: "The visiting doctor may recommend clinic, testing or hospital assessment. We can help coordinate the next step." },
    ],
    faqs: [
      { q: "Can a doctor really visit my hotel in Vietnam?", a: "Where available, yes. We help coordinate a licensed independent doctor to visit your hotel or accommodation for non-emergency care." },
      { q: "Can I request an English-speaking doctor?", a: "Yes — coordination is in English, and we do our best to match an English-speaking doctor. This depends on provider availability." },
      { q: "How quickly can a doctor come?", a: "We aim to respond as soon as possible via WhatsApp. Actual timing depends on your location and provider availability and is confirmed individually." },
      { q: "What information should I send when requesting a visit?", a: "Your name, city and accommodation, main symptoms, when they started, any current medication and allergies." },
      { q: "Can a doctor visit my Airbnb or serviced apartment?", a: "Yes, where available. The visit location can be a hotel, apartment, home or serviced residence." },
      { q: "Can the doctor prescribe medicine?", a: "Prescriptions can be coordinated where the treating doctor considers it clinically appropriate and legally permitted. Doctors Vietnam does not make medical decisions." },
      { q: "What happens if I need hospital care?", a: "If the doctor recommends hospital assessment, we can help coordinate onward care and communication with an appropriate provider." },
    ],
  },

  "online-consultation": {
    seoTitle: "Online Doctor Consultation in Vietnam | English-Speaking Telemedicine",
    metaDescription:
      "Help arranging an online consultation with an independent licensed doctor in Vietnam. English-speaking coordination for travellers, expats and international patients.",
    h1: "Online Doctor Consultation in Vietnam",
    opening:
      "Help coordinating a remote consultation with an independent licensed doctor when you'd like initial medical guidance without travelling to a clinic straight away.",
    quickAnswer: {
      title: "May be useful for",
      bullets: [
        "New non-emergency symptoms you'd like a doctor to review.",
        "Travel-illness questions or minor health concerns.",
        "Follow-up discussions after an in-person visit.",
        "Working out whether an in-person assessment or clinic visit may be needed.",
        "Travellers in areas where immediate in-person availability is limited.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Share a short summary of your concern via WhatsApp or the form." },
      { title: "Confirm a suitable time", text: "We help match you with a licensed doctor and confirm the consultation slot." },
      { title: "Speak with the doctor", text: "By video or phone, from your hotel, home or wherever you are." },
      { title: "Get clear next steps", text: "If further care is needed, we can help coordinate the next step where available." },
    ],
    coordinate: {
      title: "What an online consultation may cover",
      note: "Depending on the doctor and clinical assessment, the consultation may include:",
      items: [
        "Symptom and medical-history review",
        "Advice and reassurance for minor concerns",
        "Guidance on whether an in-person visit is needed",
        "Referral or coordination for tests, imaging or specialist review",
        "Prescription where clinically appropriate and legally permitted",
        "Follow-up planning",
      ],
    },
    commonReasons: {
      title: "Common reasons travellers use online consultation",
      note: "Please describe your situation; the doctor decides what advice is appropriate.",
      chips: [
        "Travel-related illness questions", "Cold, cough or sore throat",
        "Stomach upset or food-related symptoms", "Skin or rash questions",
        "Medication questions", "Prescription refill guidance", "Follow-up questions",
        "Advice for a child while travelling", "Second-opinion discussion",
      ],
    },
    whatToExpect: [
      { q: "What should I prepare?", a: "Your name and age, current location, main symptoms and when they started, relevant medical history, current medicines and any allergies. Photos only where the doctor asks for them." },
      { q: "Will the doctor speak English?", a: "Our coordination is English-speaking, and we work with English-speaking doctors where available." },
      { q: "Where can I take the call?", a: "Anywhere with a stable internet or phone connection — hotel room, apartment or home." },
      { q: "What if I need an in-person examination?", a: "An online consultation cannot replace an in-person examination when one is medically necessary. If needed, we can help coordinate a hotel visit, clinic or hospital appointment where available." },
      { q: "What about prescriptions?", a: "Prescriptions are only issued where the treating doctor considers it clinically appropriate and legally permitted." },
    ],
    faqs: [
      { q: "How does an online doctor consultation work?", a: "You share a short summary; we help arrange a video or phone consultation with a licensed independent doctor at a suitable time." },
      { q: "Can I speak with a doctor in English?", a: "Yes, where available. Coordination is always English-speaking." },
      { q: "Can I use the service from my hotel?", a: "Yes — from any location with a stable internet or phone connection." },
      { q: "Can a doctor prescribe medication online?", a: "Only where the treating doctor considers it clinically appropriate and legally permitted. Doctors Vietnam does not issue prescriptions." },
      { q: "What if the doctor thinks I need an examination?", a: "If an in-person assessment is needed, we can help coordinate a hotel visit, clinic or hospital appointment where available." },
      { q: "Can you arrange a clinic afterwards?", a: "Yes — we can help coordinate a suitable clinic or specialist appointment where available." },
      { q: "Is online consultation suitable for children?", a: "It can be, depending on the concern and the doctor's assessment. In some cases an in-person visit will be recommended." },
    ],
  },

  "clinic-booking": {
    seoTitle: "Clinic & Hospital Appointments in Vietnam | International Patient Support",
    metaDescription:
      "Doctors Vietnam helps international patients identify and coordinate appointments with appropriate clinics and hospitals across Vietnam, with English-speaking support.",
    h1: "Clinic & Hospital Appointments in Vietnam",
    opening:
      "Doctors Vietnam can help international patients identify and coordinate appointments with appropriate clinics and hospitals based on location, medical need and available services.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You'd like help identifying an appropriate clinic or hospital in Vietnam.",
        "You want English-speaking coordination for the booking process.",
        "You may need a diagnostic appointment, health check, laboratory testing or imaging.",
        "You're an international patient and would like help communicating with the provider.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Tell us what appointment you'd like and where you are." },
      { title: "Share the essentials", text: "Symptoms or reason, location, timing preferences and language needs." },
      { title: "We help identify a provider", text: "We check suitable clinics or hospitals in your area." },
      { title: "We coordinate the booking", text: "We help arrange the appointment and communicate with the provider." },
    ],
    coordinate: {
      title: "What we can help coordinate",
      items: [
        "Clinic appointments",
        "Hospital appointments",
        "Diagnostic appointments",
        "General health checks",
        "Laboratory testing",
        "Imaging appointments",
        "Follow-up appointments",
        "Communication support with the provider",
      ],
    },
    commonReasons: {
      title: "How we help select a provider",
      note: "Considerations may include, depending on availability:",
      chips: [
        "Medical specialty required", "Your city or location", "Provider availability",
        "Language support", "Facility capability", "Patient preference",
      ],
    },
    whatToExpect: [
      { q: "What information will I need to provide?", a: "Your name, city, the reason for the appointment, any existing diagnosis or reports, and your preferred timing and language." },
      { q: "Will there be English-speaking support?", a: "Our coordination is English-speaking, and we help match you with providers offering English-speaking care where available." },
      { q: "Where does the appointment happen?", a: "At the clinic or hospital we help arrange, unless a home/hotel option is more appropriate." },
      { q: "How is availability confirmed?", a: "We contact suitable providers and confirm the appointment individually before anything is finalised." },
      { q: "What if I need a different level of care?", a: "If the provider recommends onward care, we can help coordinate the next step where available." },
    ],
    faqs: [
      { q: "How do I find an English-speaking clinic in Vietnam?", a: "Tell us your location and reason for the appointment — we help identify suitable clinics with English-speaking care where available." },
      { q: "Can you help me choose a hospital?", a: "We can suggest options based on medical need, location, facility capability and language, subject to availability." },
      { q: "Can you arrange a same-day appointment?", a: "Sometimes, depending on the clinic and time of day. We check availability with the provider and confirm individually." },
      { q: "Can you help with medical tests?", a: "Yes — including laboratory testing, imaging and diagnostic appointments where available." },
      { q: "Can you send my medical records to the clinic?", a: "We can help pass on records you provide, with your consent, so the provider has the context they need." },
      { q: "Can someone help me communicate with the hospital?", a: "Yes — English-speaking coordination support is part of the service." },
      { q: "What if my condition is an emergency?", a: "For life-threatening emergencies in Vietnam, call 115 or go directly to the nearest hospital emergency department. We coordinate non-emergency appointments only." },
    ],
  },

  "specialist-referral": {
    seoTitle: "Find a Medical Specialist in Vietnam | English-Speaking Referral",
    metaDescription:
      "Doctors Vietnam helps coordinate access to appropriate independent medical specialists across Vietnam, subject to availability and clinical suitability.",
    h1: "Find a Medical Specialist in Vietnam",
    opening:
      "Doctors Vietnam can help coordinate access to appropriate independent specialists depending on the patient's clinical needs and location.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You need to see a specific medical specialist while in Vietnam.",
        "You'd like help identifying an English-speaking specialist where available.",
        "You'd like coordination support with referral information and appointments.",
        "You'd like help arranging a second medical opinion.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Share the specialty required and a short summary of your situation." },
      { title: "Send any records", text: "Previous reports, scan results and referral letters help us coordinate faster." },
      { title: "We check availability", text: "We look for suitable independent specialists in your preferred city." },
      { title: "We coordinate the appointment", text: "You receive clear next steps and follow-up support where appropriate." },
    ],
    coordinate: {
      title: "Specialties we may be able to coordinate",
      note: "Availability varies. This list does not mean every specialty is available through Doctors Vietnam in every city.",
      grid: [
        "Cardiology", "Dermatology", "ENT",
        "Gastroenterology", "Orthopaedics", "Neurology",
        "Ophthalmology", "Urology", "Gynaecology",
        "Paediatrics", "General surgery", "Other specialist services",
      ],
    },
    commonReasons: {
      title: "What helps us coordinate a specialist",
      note: "The more context you can share, the faster we can help.",
      chips: [
        "Symptoms / reason for referral", "Existing diagnosis where applicable",
        "Previous medical reports", "Scan or test results",
        "Referral letter if available", "Preferred city", "Preferred timing",
      ],
    },
    whatToExpect: [
      { q: "What information will I need to provide?", a: "Your name, city, the specialty needed, a short summary of the concern and any relevant reports or referral letters." },
      { q: "Will the specialist speak English?", a: "Our coordination is English-speaking, and we work with English-speaking specialists where available." },
      { q: "Where does the appointment happen?", a: "At the clinic or hospital where the specialist practises, in the city we help arrange." },
      { q: "How is availability confirmed?", a: "We contact suitable specialists and confirm availability individually before an appointment is arranged." },
      { q: "What if further care is needed?", a: "The specialist may recommend tests, imaging, hospital assessment or a different specialty. We can help coordinate the next step where available." },
    ],
    faqs: [
      { q: "How do I find a specialist doctor in Vietnam?", a: "Tell us the specialty and your city. We help identify appropriate independent specialists, subject to availability." },
      { q: "Can I see an English-speaking specialist?", a: "Where available, yes. Our coordination is always English-speaking." },
      { q: "Do I need a referral?", a: "A referral letter is helpful but not always required. We'll let you know based on the specialist and situation." },
      { q: "Can you send my existing medical records?", a: "With your consent, we can share records you provide so the specialist has clinical context." },
      { q: "Can you arrange a second medical opinion?", a: "Yes — where a suitable independent specialist is available." },
      { q: "Which cities have specialist hospitals?", a: "Ho Chi Minh City and Hanoi have the widest specialist coverage; Da Nang and other cities offer specialists in many disciplines. Availability varies." },
      { q: "Can you help with follow-up appointments?", a: "Yes — follow-up coordination is part of the service where appropriate." },
    ],
  },

  "post-discharge-support": {
    seoTitle: "Post-Hospital Discharge Support in Vietnam | Follow-Up Coordination",
    metaDescription:
      "Support for international patients discharged from a hospital or clinic in Vietnam — follow-up appointments, nursing, communication and recovery coordination.",
    h1: "Post-Hospital Discharge Support in Vietnam",
    opening:
      "Support for international patients who have been discharged from a hospital or clinic and need help coordinating the next stage of their recovery.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You have just been discharged from a hospital or clinic in Vietnam.",
        "You'd like help arranging follow-up appointments or tests.",
        "You'd like nursing support at your hotel, apartment or home.",
        "You'd like English-speaking coordination while you recover.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Tell us where you were treated and how you're recovering." },
      { title: "Share your discharge information", text: "Discharge summary, prescribed medication and any follow-up instructions." },
      { title: "We help plan the next steps", text: "Follow-up appointments, nursing, transport or tests — where appropriate." },
      { title: "Ongoing coordination", text: "We stay in touch and can help escalate if your situation changes." },
    ],
    coordinate: {
      title: "What we can help coordinate",
      items: [
        "Follow-up appointments with independent providers",
        "Nursing support at your accommodation",
        "Communication with treating providers",
        "Transport coordination for appointments",
        "Follow-up tests, imaging or laboratory work",
        "Recovery accommodation support where relevant",
        "Translation and communication support",
        "Guidance on where to contact your treating provider",
      ],
    },
    commonReasons: {
      title: "Useful information to have ready before you leave hospital",
      note: "Please follow the instructions given by your treating medical team. Doctors Vietnam does not reinterpret discharge instructions.",
      chips: [
        "Discharge summary", "Prescriptions and instructions", "Confirmed follow-up date",
        "Who to contact if symptoms change", "Copies of test or scan results",
        "Contact details of the treating provider",
      ],
    },
    whatToExpect: [
      { q: "What information will I need to provide?", a: "Your discharge details, current condition, location, and the medical team's follow-up instructions." },
      { q: "Will support be English-speaking?", a: "Yes — our coordination is English-speaking. English-speaking providers are arranged where available." },
      { q: "Where does support happen?", a: "Follow-up may be at the treating facility, an independent clinic, or nursing at your hotel/home — depending on need and availability." },
      { q: "How is availability confirmed?", a: "We contact suitable providers and confirm individually before anything is arranged." },
      { q: "What if I feel worse after discharge?", a: "Follow the medical team's instructions for who to contact. For a life-threatening emergency, call 115 or go directly to the nearest emergency department." },
    ],
    faqs: [
      { q: "Can you arrange a nurse after I leave hospital?", a: "Where available, yes. We help coordinate qualified independent nursing at your hotel, apartment or home." },
      { q: "Can you help arrange follow-up appointments?", a: "Yes — with your treating provider or another independent provider where appropriate." },
      { q: "Can someone help me communicate with the hospital?", a: "Yes — English-speaking coordination and communication support is part of the service." },
      { q: "Can you arrange transport?", a: "We can help coordinate transport for appointments where available." },
      { q: "What if I feel worse after discharge?", a: "Follow the medical team's discharge instructions first. For emergencies call 115. Otherwise, contact us and we can help coordinate a further review." },
      { q: "Can you help while I recover in a hotel?", a: "Yes — many patients recover in a hotel or serviced apartment; nursing and follow-up can often be arranged there." },
      { q: "Can you coordinate another doctor review?", a: "Yes — a further independent doctor review can be coordinated where clinically appropriate and available." },
    ],
  },

  "post-operative": {
    seoTitle: "Post-Operative Nursing in Vietnam | Private Recovery Nurse Support",
    metaDescription:
      "Doctors Vietnam helps coordinate qualified nursing support for patients recovering after surgery or medical treatment in Vietnam, subject to location, clinical requirements and nurse availability.",
    h1: "Post-Operative Nursing in Vietnam",
    opening:
      "Doctors Vietnam can help coordinate qualified nursing support for patients recovering after surgery or medical treatment, subject to location, clinical requirements and nurse availability.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You've recently had surgery or a procedure and want nursing support at your accommodation.",
        "You'd like short-term recovery help without staying in hospital longer than necessary.",
        "You need practical support while following your treating team's instructions.",
        "You would like English-speaking coordination throughout.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Tell us about the procedure, your location and support needs." },
      { title: "Share the treating team's instructions", text: "Discharge summary and any specific nursing instructions." },
      { title: "We check availability", text: "We look for a qualified independent nurse suitable for your situation." },
      { title: "We coordinate the visits", text: "You get a clear plan and can adjust as your recovery progresses." },
    ],
    coordinate: {
      title: "Support may include",
      note: "Only where clinically appropriate, authorised and within the nurse's professional scope:",
      items: [
        "General recovery monitoring",
        "Basic observations",
        "Wound-care support according to medical instructions",
        "Dressing support where ordered",
        "Mobility assistance",
        "Personal care assistance",
        "Communication with the treating team",
        "Drain or device support only when clinically instructed and within scope",
        "Medication support only according to the prescribing clinician's plan and the nurse's lawful scope of practice",
      ],
    },
    commonReasons: {
      title: "Suitable locations may include",
      note: "Depending on clinical suitability and availability.",
      chips: ["Hotel room", "Serviced apartment", "Home", "Recovery accommodation"],
    },
    whatToExpect: [
      { q: "What information will we need before arranging nursing?", a: "The procedure performed, surgery date, hospital or clinic, treating doctor's discharge instructions, current condition, location and required nursing hours." },
      { q: "Will the nurse speak English?", a: "Our coordination is English-speaking, and we work with English-speaking nurses where available." },
      { q: "Where does the nursing happen?", a: "At your hotel, apartment, home or recovery accommodation, subject to clinical suitability." },
      { q: "How is availability confirmed?", a: "We contact suitable independent nurses and confirm individually before anything is scheduled." },
      { q: "What if I need a different level of care?", a: "If a higher level of care is required, we can help coordinate onward care with an independent provider." },
    ],
    faqs: [
      { q: "Can I arrange a nurse at my hotel after surgery?", a: "Where available, yes. Many hotels and serviced apartments in major cities can accommodate nursing visits." },
      { q: "Can I arrange overnight nursing?", a: "Overnight or extended support is offered subject to availability and clinical suitability." },
      { q: "How many hours of nursing can I request?", a: "This varies with your clinical need and nurse availability — we'll discuss the plan with you before confirming." },
      { q: "Can a nurse help with wound care?", a: "Where authorised and within the nurse's scope, and according to the treating team's instructions." },
      { q: "Can the nurse communicate with my surgeon?", a: "Yes — communication with the treating team is part of good post-operative coordination." },
      { q: "How far in advance should I arrange nursing?", a: "As early as you can. Advance notice helps us confirm availability and plan around your recovery." },
      { q: "Can nursing support continue for several days?", a: "Yes, subject to availability and clinical suitability. We'll help plan the schedule with you." },
    ],
  },
  "elderly-care": {
    seoTitle: "Elderly Care Support in Vietnam | Private Carer Coordination",
    metaDescription:
      "Doctors Vietnam helps coordinate qualified independent carers to support older adults with daily activities, companionship and wellbeing in Vietnam, subject to availability.",
    h1: "Elderly Care Support in Vietnam",
    opening:
      "Doctors Vietnam can help coordinate qualified independent carers to support older adults with daily activities, companionship and wellbeing, subject to location and carer availability.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You have an older parent, relative or partner visiting or living in Vietnam who needs extra help.",
        "You'd like short-term or ongoing daily assistance without a hospital stay.",
        "You want English-speaking coordination while arranging care.",
        "You need respectful, discreet in-home support at a hotel, apartment or residence.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Tell us about the person, their needs and their location." },
      { title: "Share the essentials", text: "Any health conditions, medication routine and preferred support hours." },
      { title: "We check availability", text: "We look for a suitable independent carer for your situation." },
      { title: "We coordinate visits", text: "You get a clear plan and can adjust as needs change." },
    ],
    coordinate: {
      title: "Support may include",
      note: "Only where clinically appropriate, authorised and within the carer's professional scope:",
      items: [
        "Daily activities and personal care assistance",
        "Companionship and wellbeing check-ins",
        "Medication reminders (not medication administration unless clinically authorised)",
        "Mobility and transfer support",
        "Meal and hydration support",
        "Routine and light household support",
        "Coordination with family members",
        "Communication with treating doctors where appropriate",
      ],
    },
    commonReasons: {
      title: "Common reasons families arrange elderly support",
      chips: [
        "Older parent visiting Vietnam", "Recovering after illness",
        "Post-hospital assistance", "Companionship while family works",
        "Mobility limitations", "Short-term travel support",
        "Additional help alongside family",
      ],
    },
    whatToExpect: [
      { q: "What information will we need?", a: "The person's name, age, general health, location, medication routine and the hours or type of support you'd like." },
      { q: "Will the carer speak English?", a: "Coordination is English-speaking; English-speaking carers are matched where available." },
      { q: "Where does the support happen?", a: "At a hotel, apartment, home or serviced residence — wherever the person is staying." },
      { q: "How is availability confirmed?", a: "We contact suitable independent carers and confirm individually before scheduling." },
      { q: "What if a higher level of care is needed?", a: "If a nurse, doctor or hospital assessment is needed, we can help coordinate the next step." },
    ],
    faqs: [
      { q: "Can you arrange a carer at my parent's hotel?", a: "Where available, yes. Coordination for hotels, apartments and residences is part of the service." },
      { q: "How many hours can I book?", a: "Hours vary with the situation and carer availability. We'll discuss a plan before confirming." },
      { q: "Can the carer give medication?", a: "Reminders yes. Actual administration is only where clinically authorised and within the carer's professional scope." },
      { q: "Can carers help with mobility?", a: "Basic mobility and transfer assistance can be coordinated where appropriate and within scope." },
      { q: "Can I arrange ongoing support?", a: "Yes — flexible short-term or ongoing schedules are possible, subject to availability." },
      { q: "What if my parent needs a doctor?", a: "We can help coordinate an independent doctor visit or clinic appointment as a separate service." },
    ],
  },

  "mother-baby": {
    seoTitle: "Mother & Baby Support in Vietnam | Postnatal & Newborn Care",
    metaDescription:
      "Doctors Vietnam helps coordinate experienced independent carers to support new mothers and newborns in Vietnam with practical postnatal care, subject to availability.",
    h1: "Mother & Baby Support in Vietnam",
    opening:
      "Doctors Vietnam can help coordinate experienced independent nurses and carers to support new mothers and newborns with practical, gentle care during the early days, subject to availability.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You're a new mother wanting a little extra hands-on help at home or at your hotel.",
        "You'd like practical, respectful postnatal support in Vietnam.",
        "You need short-term reassurance during the newborn phase.",
        "You want English-speaking coordination through the process.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Tell us your due date or current stage and what would help most." },
      { title: "Share the essentials", text: "Delivery details (planned or completed), any medical notes and location." },
      { title: "We check availability", text: "We look for a suitable maternity-experienced independent carer." },
      { title: "We coordinate visits", text: "A clear plan for daytime or short-term support, adjustable as you go." },
    ],
    coordinate: {
      title: "Support may include",
      note: "Only where appropriate and within the carer's or nurse's professional scope:",
      items: [
        "Practical newborn care support",
        "Feeding, bathing and sleep-routine assistance",
        "Reassurance and gentle guidance for new mothers",
        "Postnatal wellbeing check-ins",
        "Light household and routine support",
        "Coordination with the treating obstetrician or paediatrician where appropriate",
      ],
    },
    commonReasons: {
      title: "Common reasons families ask for support",
      chips: [
        "First-time parents", "Recovering after birth", "Extra help at home",
        "Family visiting Vietnam", "Overnight rest for parents (where available)",
        "Assistance during partner travel", "Practical postnatal reassurance",
      ],
    },
    whatToExpect: [
      { q: "What information will we need?", a: "Delivery status, baby's age, any medical notes, location, preferred hours and language." },
      { q: "Will the carer speak English?", a: "Coordination is English-speaking; English-speaking carers are matched where available." },
      { q: "Where does support happen?", a: "At your home, apartment or hotel — wherever mother and baby are recovering." },
      { q: "How is availability confirmed?", a: "We contact suitable independent carers and confirm individually before scheduling." },
      { q: "What if medical concerns arise?", a: "Please follow guidance from your treating obstetrician or paediatrician. We can help coordinate a doctor review as a separate service." },
    ],
    faqs: [
      { q: "Can I arrange support at my hotel after giving birth in Vietnam?", a: "Where available, yes. Hotel and serviced-apartment support is common." },
      { q: "Can support include help with feeding?", a: "Practical feeding and routine support can be coordinated where appropriate." },
      { q: "Is overnight support possible?", a: "Sometimes, subject to availability. Please ask when you contact us." },
      { q: "Can you coordinate a paediatrician if needed?", a: "Yes — a paediatrician appointment can be arranged as a separate service." },
      { q: "How soon can I arrange help?", a: "We respond as soon as possible via WhatsApp; actual timing depends on carer availability." },
      { q: "Is medical postnatal care included?", a: "No. We coordinate practical support. Any medical care is provided by an independent doctor or midwife." },
    ],
  },

  "short-term": {
    seoTitle: "Short-Term Home Nursing in Vietnam | Flexible Private Nurse",
    metaDescription:
      "Doctors Vietnam helps coordinate short-term independent nursing support at hotels, apartments or homes in Vietnam, subject to clinical needs, location and nurse availability.",
    h1: "Short-Term Home Nursing in Vietnam",
    opening:
      "Doctors Vietnam can help coordinate short-term independent nursing support at your hotel, apartment or home — flexible, reliable and easy to arrange, subject to availability.",
    quickAnswer: {
      title: "Is this service right for me?",
      bullets: [
        "You need practical nursing support at your accommodation for a short period.",
        "You're recovering from illness or a procedure and don't need a hospital stay.",
        "You want flexible scheduling that adapts as your needs change.",
        "You'd like English-speaking coordination throughout.",
      ],
    },
    howItWorks: [
      { title: "Contact Doctors Vietnam", text: "Tell us the situation, location and hours you have in mind." },
      { title: "Share the essentials", text: "Current condition, any doctor's instructions and required support type." },
      { title: "We check availability", text: "We look for a qualified independent nurse suited to your situation." },
      { title: "We coordinate visits", text: "You get a flexible plan that can be adjusted as things progress." },
    ],
    coordinate: {
      title: "Support may include",
      note: "Only where clinically appropriate, authorised and within the nurse's professional scope:",
      items: [
        "Short-term nursing visits at your accommodation",
        "Basic observations and wellbeing checks",
        "Wound-care support only according to medical instructions",
        "Mobility and personal care assistance",
        "Medication support only according to the prescribing clinician's plan and the nurse's lawful scope of practice",
        "Communication with the treating doctor where appropriate",
        "Flexible day, half-day or hourly scheduling where available",
      ],
    },
    commonReasons: {
      title: "Common reasons travellers use short-term nursing",
      chips: [
        "Recovering after illness", "Short-term post-procedure care", "Elderly relative visiting",
        "Assistance for a family member", "Peace of mind while travelling",
        "Bridging care between appointments", "Practical recovery support",
      ],
    },
    whatToExpect: [
      { q: "What information will we need?", a: "Current condition, any treating-doctor instructions, location, preferred hours and language." },
      { q: "Will the nurse speak English?", a: "Coordination is English-speaking; English-speaking nurses are matched where available." },
      { q: "Where does the nursing happen?", a: "At your hotel, apartment or home, subject to clinical suitability." },
      { q: "How is availability confirmed?", a: "We contact suitable independent nurses and confirm individually before scheduling." },
      { q: "What if I need more support later?", a: "The plan can be extended or reduced as your situation changes, subject to availability." },
    ],
    faqs: [
      { q: "Can I book a nurse for just a few hours?", a: "Where available, yes. Flexible short blocks are common." },
      { q: "Can I extend the nursing if I need to?", a: "Yes, subject to nurse availability and clinical suitability." },
      { q: "Can the nurse coordinate with my doctor?", a: "Yes — communication with the treating doctor is part of good short-term nursing coordination." },
      { q: "Can nursing be arranged at a hotel?", a: "Where available, yes. Hotel and serviced-apartment nursing is common in major cities." },
      { q: "Is overnight nursing possible?", a: "Sometimes, subject to availability and clinical suitability." },
      { q: "What is not included?", a: "Any care that falls outside a nurse's professional scope. Doctors Vietnam does not diagnose or provide medical treatment." },
    ],
  },
};

export const getServiceContent = (slug) => SERVICE_CONTENT[slug];
